import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Row, Col, Button, Input, Badge } from "reactstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

/* ================== Tema/cores ================== */
const COLORS = {
  bg: "#0B0B0F",
  surface: "#121317",
  border: "#26272B",
  text: "#ECECF3",
  sub: "#A3A6AF",
  primary: "#913ef8",
  pink: "#e14eca",
  yellow: "#F6E05E",
};

/* ================== Helpers ================== */
const formatBRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

const STRATEGIES = {
  conservador: { label: "Conservador", rate: 0.05, stopMult: 2, desc: "Crescimento suave com risco reduzido (5% ao dia, Stop = 2x da meta)." },
  moderado: { label: "Moderado", rate: 0.10, stopMult: 2, desc: "Equilíbrio entre risco e retorno (10% ao dia, Stop = 2x da meta)." },
  agressivo: { label: "Agressivo", rate: 0.15, stopMult: 2, desc: "Retorno alto com maior risco (15% ao dia, Stop = 2x da meta)." },
};

/* ================== Componente ================== */
const InvestmentTable = () => {
  // estado
  const [banca, setBanca] = useState(1000);
  const [strategy, setStrategy] = useState("conservador");
  const [days, setDays] = useState(30);
  const [data, setData] = useState([]);

  // carregar/salvar preferências
  useEffect(() => {
    const stored = localStorage.getItem("invest_prefs");
    if (stored) {
      try {
        const { banca, strategy, days } = JSON.parse(stored);
        if (banca) setBanca(banca);
        if (strategy) setStrategy(strategy);
        if (days) setDays(days);
      } catch {}
    }
  }, []);
  useEffect(() => {
    localStorage.setItem(
      "invest_prefs",
      JSON.stringify({ banca, strategy, days })
    );
  }, [banca, strategy, days]);

  const valid = useMemo(() => Number(banca) > 0 && days >= 1 && days <= 365, [banca, days]);

  const simulate = useCallback(() => {
    const rows = [];
    let saldoAtual = Number(banca);
    const startDate = new Date();
    const cfg = STRATEGIES[strategy] || STRATEGIES.moderado;

    for (let i = 0; i < Number(days); i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);

      const meta = saldoAtual * cfg.rate;
      const stopLoss = meta * cfg.stopMult;
      const saldoFinal = saldoAtual + meta;

      rows.push({
        date: currentDate.toLocaleDateString("pt-BR"),
        saldoAtual,
        meta,
        stopLoss,
        saldoFinal,
      });

      saldoAtual = saldoFinal;
    }
    return rows;
  }, [banca, days, strategy]);

  const handleCalcular = () => {
    if (!valid) return;
    setData(simulate());
  };

  const handleDetalhes = () => {
    const html = `
      <div style="text-align:left">
        <table class="table table-sm table-striped" style="background:#0f1115;color:#ECECF3;border:1px solid #26272B">
          <thead style="position:sticky;top:0;background:#121317;">
            <tr>
              <th style="font-size:12px;">Dia</th>
              <th style="font-size:12px;">Data</th>
              <th style="font-size:12px;">Saldo Atual</th>
              <th style="font-size:12px;">Meta</th>
              <th style="font-size:12px;">Stop Loss</th>
              <th style="font-size:12px;">Saldo Final</th>
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row, i) => `
              <tr>
                <td style="font-size:12px;">${i + 1}</td>
                <td style="font-size:12px;">${row.date}</td>
                <td style="font-size:12px;">${formatBRL(row.saldoAtual)}</td>
                <td style="font-size:12px;">${formatBRL(row.meta)}</td>
                <td style="font-size:12px;">${formatBRL(row.stopLoss)}</td>
                <td style="font-size:12px;">${formatBRL(row.saldoFinal)}</td>
              </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;
    MySwal.fire({
      title: "Detalhes da Gestão",
      html,
      width: "80%",
      background: "#121317",
      color: "#ECECF3",
      confirmButtonText: "Fechar",
      buttonsStyling: false,
      customClass: {
        popup: "swal2-aviator-popup",
        confirmButton: "swal2-aviator-confirm",
      },
    });
  };

  const handleExportCSV = () => {
    if (!data.length) return;
    const header = ["Dia", "Data", "Saldo Atual", "Meta", "Stop Loss", "Saldo Final"];
    const lines = data.map((r, i) => [
      i + 1,
      r.date,
      r.saldoAtual.toFixed(2).replace(".", ","),
      r.meta.toFixed(2).replace(".", ","),
      r.stopLoss.toFixed(2).replace(".", ","),
      r.saldoFinal.toFixed(2).replace(".", ","),
    ]);
    const csv = [header, ...lines].map((row) => row.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gestao_banca_${strategy}_${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setBanca(1000);
    setStrategy("conservador");
    setDays(30);
    setData([]);
  };

  const cfg = STRATEGIES[strategy];
  const metaHoje = Number(banca) * cfg.rate;
  const stopHoje = metaHoje * cfg.stopMult;

  const lucroTotal = useMemo(() => {
    if (!data.length) return 0;
    return data[data.length - 1].saldoFinal - Number(banca);
  }, [data, banca]);

  const saldoProj = useMemo(() => (data.length ? data[data.length - 1].saldoFinal : Number(banca)), [data, banca]);

  return (
    <div style={{ color: COLORS.text }}>
      <style>{`
        .chip { padding: 6px 10px; background:#1b1c21; border:1px solid ${COLORS.border}; color:${COLORS.text}; border-radius:10px; cursor:pointer; }
        .chip:hover { filter:brightness(1.1) }
        .swal2-aviator-confirm{
          background:${COLORS.primary}!important;color:#fff!important;border:none!important;
          padding:8px 16px;border-radius:8px;font-weight:600;
        }
        .table thead th { border-bottom: 1px solid ${COLORS.border}; }
        .table td, .table th { border-top: 1px solid ${COLORS.border}; }
      `}</style>

      {/* Cabeçalho */}
      <Row className="align-items-center">
        <Col md="12">
          <h3 className="card-title m-0">Gestão de Banca</h3>
          <div style={{ color: COLORS.sub, fontSize: 12 }}>Simulação composta diária com meta e stop por perfil.</div>
          <hr className="divider divider-light divider-dashed" />
        </Col>
      </Row>

      {/* Controles */}
      <Row className="align-items-end">
        <Col md="4" className="mb-3">
          <label style={{ color: COLORS.sub }}>Valor da Banca</label>
          <div className="d-flex" style={{ gap: 8 }}>
            <Input
              type="number"
              min="1"
              step="1"
              className="form-control p-2"
              placeholder="Valor da banca"
              value={banca}
              onChange={(e) => setBanca(e.target.value)}
              style={{ background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}` }}
            />
          </div>
          <div className="mt-1" style={{ color: COLORS.sub, fontSize: 12 }}>
            Atual: <strong style={{ color: COLORS.yellow }}>{formatBRL(banca)}</strong>
          </div>
        </Col>

        <Col md="4" className="mb-3">
          <label style={{ color: COLORS.sub }}>Perfil</label>
          <Input
            type="select"
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
            className="form-control"
            style={{ background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}` }}
          >
            <option value="conservador">Conservador (5% ao dia)</option>
            <option value="moderado">Moderado (10% ao dia)</option>
            <option value="agressivo">Agressivo (15% ao dia)</option>
          </Input>
          <div className="mt-1" style={{ color: COLORS.sub, fontSize: 12 }}>{cfg.desc}</div>
        </Col>

        <Col md="4" className="mb-3">
          <label style={{ color: COLORS.sub }}>Período (dias)</label>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <Input
              type="range"
              min="1"
              max="365"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              style={{ accentColor: COLORS.primary, flex: 1 }}
            />
            <Input
              type="number"
              min="1"
              max="365"
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value))))}
              style={{ width: 90, background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}` }}
            />
          </div>
        </Col>
      </Row>

      {/* Ações */}
      <Row className="mt-2">
        <Col md="12" className="d-flex" style={{ gap: 8 }}>
          <Button
            color="primary"
            onClick={handleCalcular}
            disabled={!valid}
            style={{ background: COLORS.primary, border: "none", minWidth: 140 }}
          >
            Calcular
          </Button>
          <Button color="secondary" onClick={reset} style={{ background: "#1a1b20", border: `1px solid ${COLORS.border}` }}>
            Resetar
          </Button>

          {data.length > 0 && (
            <>
              <Button color="info" onClick={handleDetalhes} style={{ background: "#2a2c34", border: `1px solid ${COLORS.border}` }}>
                Detalhes
              </Button>
              <Button color="success" onClick={handleExportCSV} style={{ background: "#2f8f46", border: "none" }}>
                Exportar CSV
              </Button>
            </>
          )}
        </Col>
      </Row>

      {/* Resumos */}
      <hr className="divider divider-light divider-dashed" />
      <Row className="mb-2">
        <Col md="6" className="mb-3">
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
            <div style={{ color: COLORS.sub, fontSize: 12, marginBottom: 6 }}>Meta & Stop (Dia 1)</div>
            <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
              <Badge color="primary" style={{ background: "#1b1c21", border: `1px solid ${COLORS.border}` }}>
                Meta: <span className="ml-1" style={{ color: COLORS.yellow }}>{formatBRL(metaHoje)}</span>
              </Badge>
              <Badge color="primary" style={{ background: "#1b1c21", border: `1px solid ${COLORS.border}` }}>
                Stop: <span className="ml-1" style={{ color: "#ff8a8a" }}>{formatBRL(stopHoje)}</span>
              </Badge>
              <span style={{ color: COLORS.sub, fontSize: 12 }}>
                (Base em {formatBRL(banca)} • {STRATEGIES[strategy].rate * 100}%)
              </span>
            </div>
          </div>
        </Col>
        <Col md="6" className="mb-3">
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
            <div style={{ color: COLORS.sub, fontSize: 12, marginBottom: 6 }}>Projeção do Período</div>
            <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
              <Badge color="primary" style={{ background: "#1b1c21", border: `1px solid ${COLORS.border}` }}>
                Lucro: <span className="ml-1" style={{ color: COLORS.yellow }}>{formatBRL(lucroTotal)}</span>
              </Badge>
              <Badge color="primary" style={{ background: "#1b1c21", border: `1px solid ${COLORS.border}` }}>
                Saldo Projetado: <span className="ml-1" style={{ color: COLORS.text }}>{formatBRL(saldoProj)}</span>
              </Badge>
              <span style={{ color: COLORS.sub, fontSize: 12 }}>
                {days} {days === 1 ? "dia" : "dias"} • Perfil {STRATEGIES[strategy].label}
              </span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Tabela simples (quando calculado) */}
      {data.length > 0 && (
        <>
          <div style={{ height: 420, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <table className="table table-striped mb-0" style={{ color: COLORS.text }}>
              <thead style={{ position: "sticky", top: 0, background: COLORS.surface, zIndex: 1 }}>
                <tr>
                  <th>Dia</th>
                  <th>Data</th>
                  <th>Saldo Atual</th>
                  <th>Meta</th>
                  <th>Stop Loss</th>
                  <th>Saldo Final</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{row.date}</td>
                    <td>{formatBRL(row.saldoAtual)}</td>
                    <td>{formatBRL(row.meta)}</td>
                    <td>{formatBRL(row.stopLoss)}</td>
                    <td>{formatBRL(row.saldoFinal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ color: COLORS.sub, fontSize: 12, marginTop: 8 }}>
            Dica: ajuste os dias e o perfil para comparar cenários. Use o CSV para salvar seu plano.
          </div>
        </>
      )}
    </div>
  );
};

export default InvestmentTable;
